// "Spent on" dates for expenses (Sept 2026).
//
// Every expense has two dates:
//   - date      → when the money was spent. Entered by the person adding it
//                 (defaults to today), filled from the receipt by AI scan and
//                 AI Mode, and editable later.
//   - createdAt → when it was posted to Spliiit. Stamped by the server,
//                 never editable, always shown.
//
// A spent date is stored as noon UTC of that calendar day so it shows as the
// same day everywhere from Hawaii to Eastern Europe.

const YMD = /^\d{4}-\d{2}-\d{2}$/;

/** Today's calendar date in the viewer's local time, as YYYY-MM-DD. */
export function todayYmd(now = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** YYYY-MM-DD → stored ISO (noon UTC). Null if it isn't a plausible spent date:
 *  not a real day, more than a day in the future, or over 10 years back. */
export function spentDateToIso(ymd: unknown): string | null {
  if (typeof ymd !== "string" || !YMD.test(ymd)) return null;
  const t = Date.parse(`${ymd}T12:00:00.000Z`);
  if (Number.isNaN(t)) return null;
  const iso = new Date(t).toISOString();
  if (iso.slice(0, 10) !== ymd) return null; // e.g. 2026-02-31
  const day = 24 * 60 * 60 * 1000;
  if (t > Date.now() + 1.5 * day) return null;
  if (t < Date.now() - 3653 * day) return null;
  return iso;
}

/** A stored expense date (ISO or YYYY-MM-DD) → YYYY-MM-DD for a date input. */
export function isoToYmd(value: string): string {
  if (YMD.test(value)) return value;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? todayYmd() : todayYmd(d);
}

/** Receipt dates come back as text like "Apr 20, 2026" or "2026-04-20".
 *  Returns YYYY-MM-DD when it's a plausible spent date, else null. */
export function receiptDateToYmd(text: string | null | undefined): string | null {
  if (!text) return null;
  const trimmed = text.trim();
  if (YMD.test(trimmed)) return spentDateToIso(trimmed) ? trimmed : null;
  const t = Date.parse(trimmed);
  if (Number.isNaN(t)) return null;
  const ymd = todayYmd(new Date(t));
  return spentDateToIso(ymd) ? ymd : null;
}

/** True when the spent day and the posted day differ (in the viewer's time). */
export function postedOnDifferentDay(spent: string, createdAt: string | null | undefined): boolean {
  if (!createdAt) return false;
  return isoToYmd(spent) !== todayYmd(new Date(createdAt));
}
